<template>
  <div id="CenterSignin" class="flex flex-col px-7 lg:px-0 lg:max-w-md pb-8">

    <!-- Icon + Subtitle inline -->
    <div class="flex items-center gap-3 mb-6">
      <div class="w-10 h-10 rounded-xl flex items-center justify-center signin-icon shrink-0">
        <BuildingStorefrontIcon class="h-5 w-5" style="color: #06C8FF;" />
      </div>
      <p class="text-[13px] leading-relaxed text-muted-foreground">
        {{ $t('centersignin.subtitle') }}
      </p>
    </div>

    <!-- Form -->
    <form @submit.prevent="connect" class="flex flex-col gap-4">
      <!-- Global error banner -->
      <div v-if="error" class="flex items-center gap-3 px-4 py-3 rounded-xl signin-error-banner">
        <ExclamationTriangleIcon class="h-4 w-4 shrink-0" style="color: #E3211C;" />
        <p class="text-[13px] font-medium" style="color: #E3211C;">
          {{ $t(`errors.${error}`) }}
        </p>
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-[13px] font-semibold text-foreground pl-0.5">
          {{ $t('centersignin.form.login') }}
        </label>
        <input type="text" name="center_login" required v-model="login"
          class="w-full h-[46px] px-4 rounded-xl text-[14px] font-medium bg-card text-foreground signin-input transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-apex-blue-500/40 focus:border-apex-blue-500"
          :placeholder="$t('centersignin.form.login')" />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="text-[13px] font-semibold text-foreground pl-0.5">
          {{ $t('centersignin.form.password') }}
        </label>
        <input type="password" name="center_password" required v-model="password"
          autocomplete="center-password"
          class="w-full h-[46px] px-4 rounded-xl text-[14px] font-medium bg-card text-foreground signin-input transition duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-apex-blue-500/40 focus:border-apex-blue-500"
          :placeholder="$t('centersignin.form.password')" />
      </div>

      <button type="submit" :disabled="sending"
        class="flex items-center justify-center gap-2.5 w-full h-[52px] text-white font-bold text-[15px] rounded-2xl active:scale-[0.97] transition-all duration-150 mt-2 signin-cta"
        :class="{ 'opacity-60 pointer-events-none': sending }">
        <svg v-if="sending" class="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none"
          viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
          </path>
        </svg>
        <ArrowRightIcon v-else class="h-5 w-5 stroke-[2.5]" />
        {{ $t('centersignin.form.signin') }}
      </button>
    </form>
  </div>
</template>

<script setup lang="ts">
import { ArrowRightIcon, BuildingStorefrontIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import { ref, inject, Ref } from 'vue';
import { useRouter } from 'vue-router';
import { useUserManagerStore } from '../stores/userManagerStore';

const router = useRouter();
const userManagerStore = useUserManagerStore();
const connectionProgress = inject<Ref<number>>('connectionProgress')!;
const login = ref('');
const password = ref('');
const error = ref();
const sending = ref(false);

const knownErrorKeys = ['invalid_credentials', 'invalid_password', 'device_blocked', 'service_unavailable', 'connection_error', 'unknown_error'];

const normalizeErrorKey = (message: string): string => {
  if (knownErrorKeys.includes(message)) return message;
  const lower = message.toLowerCase();
  if (lower.includes('unavailable') || lower.includes('503')) return 'service_unavailable';
  if (lower.includes('network') || lower.includes('timeout') || lower.includes('econnrefused')) return 'connection_error';
  return 'unknown_error';
};

const connect = async () => {
  error.value = undefined;
  sending.value = true;
  connectionProgress.value = 2;
  const resp = await userManagerStore.connectToCenter(login.value, password.value).catch((err) => err);

  if (resp instanceof Error) {
    sending.value = false;
    error.value = normalizeErrorKey(resp.message);

    if (resp.message === "invalid_device") {
      await userManagerStore.signinRedirect();
    }

    return;
  }

  router.push({ name: 'CenterUserSignin' });
};
</script>

<style scoped>
.signin-icon {
  background: rgba(6, 200, 255, 0.08);
  border: 1.5px solid rgba(6, 200, 255, 0.12);
}
.signin-input {
  border: 1.5px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}
.signin-error-banner {
  background-color: rgba(227, 33, 28, 0.06);
  border: 1px solid rgba(227, 33, 28, 0.12);
}
.signin-cta {
  background: linear-gradient(135deg, #06C8FF 0%, #009FE3 100%);
  box-shadow: 0 6px 24px rgba(6, 200, 255, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.18);
}

/* Dark mode */
:is(.dark *).signin-icon {
  background: rgba(6, 200, 255, 0.12);
  border: 1.5px solid rgba(6, 200, 255, 0.18);
}
:is(.dark *).signin-input {
  border: 1.5px solid rgba(6, 200, 255, 0.10);
  box-shadow: 0 1px 6px rgba(6, 200, 255, 0.04);
}
:is(.dark *).signin-error-banner {
  background-color: rgba(227, 33, 28, 0.10);
  border: 1px solid rgba(227, 33, 28, 0.20);
}
:is(.dark *).signin-cta {
  box-shadow: 0 4px 16px rgba(6, 200, 255, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.10);
}
</style>
